import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";
import { PUBLIC_PATHS } from "./api";

const AuthCtx = createContext(null);

const _isPublicPage = () => PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refetch silencioso del usuario (permisos, rol, zonas) desde el backend.
  // Se llama en el mount inicial y cada vez que la pestaña vuelve a estar visible.
  const silentRefresh = () => {
    const token = localStorage.getItem("afc_token");
    if (!token || _isPublicPage()) return Promise.resolve(null);
    return api
      .get("/v1/auth/me")
      .then((r) => {
        setUser((prev) => {
          const next = r.data;
          try { localStorage.setItem("afc_user", JSON.stringify(next)); } catch { /* ignore */ }
          // Evita re-render si los permisos+rol+zonas no cambiaron
          if (prev && prev.role === next.role
              && JSON.stringify(prev.permissions || []) === JSON.stringify(next.permissions || [])
              && JSON.stringify(prev.zone_ids || []) === JSON.stringify(next.zone_ids || [])) {
            return prev;
          }
          return next;
        });
        return r.data;
      })
      .catch(() => null);
  };

  useEffect(() => {
    // En rutas públicas no intentar hidratar sesión
    if (_isPublicPage()) {
      setLoading(false);
      return;
    }
    const cached = localStorage.getItem("afc_user");
    const token = localStorage.getItem("afc_token");
    if (cached && token) {
      const parsed = JSON.parse(cached);
      // Solo pintamos el user cacheado si YA tiene permissions hidratados.
      // Si no, esperamos a /auth/me para evitar el sidebar vacío.
      if (Array.isArray(parsed.permissions)) {
        setUser(parsed);
      }
      api
        .get("/v1/auth/me")
        .then((r) => {
          setUser(r.data);
          localStorage.setItem("afc_user", JSON.stringify(r.data));
        })
        .catch(() => {
          // Fallback al endpoint legacy del MVP
          api.get("/auth/me")
            .then((r) => {
              setUser(r.data);
              localStorage.setItem("afc_user", JSON.stringify(r.data));
            })
            .catch(() => {
              localStorage.removeItem("afc_token");
              localStorage.removeItem("afc_refresh");
              localStorage.removeItem("afc_user");
              setUser(null);
            });
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Refresco dinámico de permisos:
  // Cuando la pestaña vuelve a estar visible o gana foco, re-fetch /auth/me para
  // reflejar cambios de rol/permisos hechos por un admin en otra sesión.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") silentRefresh();
    };
    const onFocus = () => silentRefresh();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  /**
   * Login H1: si el usuario tiene MFA activado, retorna { mfa_required: true, user_id }
   * Si pasa todo, persiste tokens y usuario.
   */
  const login = async (email, password, mfa_code = null) => {
    const { data } = await api.post("/v1/auth/login", { email, password, mfa_code });
    if (data.mfa_required) {
      return { mfa_required: true, user_id: data.user_id };
    }
    localStorage.setItem("afc_token", data.access_token);
    localStorage.setItem("afc_refresh", data.refresh_token);
    localStorage.setItem("afc_user", JSON.stringify(data.user));
    // Sidebar groups: contraídos al iniciar sesión (reset a estado inicial)
    try { sessionStorage.removeItem("sidebar-groups"); } catch {}
    setUser(data.user);
    return { user: data.user };
  };

  const logout = async () => {
    // v101.27.0 · Durante «Entrar como», cerrar sesión = volver a la cuenta del super_admin
    if (localStorage.getItem("afc_imp_backup")) {
      const m = await import("./impersonation");
      return m.endImpersonation();
    }
    try {
      await api.post("/v1/auth/logout");
    } catch (e) {}
    localStorage.removeItem("afc_token");
    localStorage.removeItem("afc_refresh");
    localStorage.removeItem("afc_user");
    setUser(null);
    window.location.href = "/login";
  };

  const refreshMe = async () => {
    const { data } = await api.get("/v1/auth/me");
    setUser(data);
    localStorage.setItem("afc_user", JSON.stringify(data));
    return data;
  };

  // ★ Fix Feb-2026 · Bus global de invalidación de permisos.
  // Cualquier página (AdminRoles, AdminUsuarios) puede invocar `refreshPerms()`
  // tras guardar cambios para que el usuario logueado vea el efecto inmediato
  // (menú lateral + rutas protegidas + botones condicionales) sin recargar.
  const refreshPerms = async () => {
    try {
      const data = await refreshMe();
      // Notificar a cualquier listener que use bus de eventos
      window.dispatchEvent(new CustomEvent("afc:perms-refreshed", { detail: data }));
      return data;
    } catch (e) {
      return null;
    }
  };

  // Helper para verificar permisos en componentes/UI
  // v3.6.3 · super_admin tiene bypass total (siempre TRUE)
  const hasPerm = (code) => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    const perms = user.permissions || [];
    if (perms.includes("*")) return true;
    return perms.includes(code);
  };

  // Helper para verificar múltiples permisos (todos requeridos)
  const hasAllPerms = (...codes) => codes.every((c) => hasPerm(c));
  // Helper para verificar al menos uno
  const hasAnyPerm = (...codes) => codes.some((c) => hasPerm(c));

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refreshMe, refreshPerms, hasPerm, hasAllPerms, hasAnyPerm }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Mapping completo H1 (9 roles) + retrocompatibilidad MVP
export const ROLES = {
  // H1
  super_admin: "Super Administrador",
  admin_zone: "Administrador de Zona",
  supervisor: "Supervisor Operativo",
  fiscalizer: "Fiscalizador",
  finance: "Financiero",
  technician: "Técnico",
  contractor: "Contratista",
  support: "Soporte Técnico",
  auditor: "Auditor Externo",
  // Legacy MVP (alias por compatibilidad de UI)
  admin: "Administrador",
  tecnico: "Técnico",
  fiscalizador: "Fiscalizador",
  financiero: "Financiero",
};

// Helper para verificar rol con compatibilidad
export const isAdmin = (role) => ["super_admin", "admin_zone", "admin"].includes(role);
export const isSupervisor = (role) => ["supervisor", "super_admin", "admin"].includes(role);
export const isTechnician = (role) => ["technician", "tecnico"].includes(role);
